/**
 * Normalize text for fuzzy matching against document content.
 * Strips markdown syntax and collapses whitespace to improve match success
 * when matching chunk text (markdown) against rendered document text (DOCX/PDF).
 */

/**
 * Strip HTML tags from text.
 */
function stripHtml(text: string): string {
  return text.replace(/<[^>]+>/g, " ")
}

/**
 * Strip common markdown syntax from text.
 */
function stripMarkdown(text: string): string {
  return (
    stripHtml(text)
      // Bold and italic
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/\*(.+?)\*/g, "$1")
      .replace(/__(.+?)__/g, "$1")
      .replace(/_(.+?)_/g, "$1")
      // Headers
      .replace(/^#{1,6}\s+/gm, "")
      // List markers
      .replace(/^[\s]*[-*+]\s+/gm, "")
      .replace(/^[\s]*\d+\.\s+/gm, "")
      // Blockquote
      .replace(/^>\s+/gm, "")
      // Table and code
      .replace(/\|/g, " ")
      .replace(/`([^`]+)`/g, "$1")
      // Horizontal rule
      .replace(/^[-*_]{3,}\s*$/gm, "")
  )
}

/**
 * Collapse multiple whitespace (spaces, newlines, tabs) into a single space.
 */
function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim()
}

function collapseWhitespacePreserveEdges(text: string): string {
  return text.replace(/\s+/g, " ")
}

type NormalizeForMatchingOptions = {
  trimEdges?: boolean
}

/**
 * Reversible normalization for DOM index mapping.
 * Only collapses whitespace so normalized indices can be mapped back reliably.
 */
export function normalizeContainerForMapping(
  text: string,
  options: NormalizeForMatchingOptions = {}
): string {
  if (!text || typeof text !== "string") return ""
  const { trimEdges = true } = options
  return trimEdges ? collapseWhitespace(text) : collapseWhitespacePreserveEdges(text)
}

/**
 * Normalize text for fuzzy matching against document content.
 * Use this on both the chunk text and the document text before matching.
 *
 * Minimal normalization approach: only strip markdown and collapse whitespace.
 * Preserve punctuation to maintain accurate boundaries and prevent overmatching.
 */
export function normalizeForMatching(
  text: string,
  options: NormalizeForMatchingOptions = {}
): string {
  if (!text || typeof text !== "string") return ""
  const { trimEdges = true } = options
  const stripped = stripMarkdown(text)
  return trimEdges ? collapseWhitespace(stripped) : collapseWhitespacePreserveEdges(stripped)
}
